import { Flex } from "@redotech/redo-web/flex";
import { FormSidePanel } from "@redotech/redo-web/form-builder/form-side-panel";
import { BgColorValue } from "@redotech/redo-web/theme/box";
import * as React from "react";
import { useEffect, useState } from "react";
import { getRightPanelClass } from "./flow-editor";
import * as flowEditorStyles from "./flow-editor.module.css";

export interface RightPanelWithAnimationProps {
  show: boolean;
  InspectorComponent: React.ReactNode;
  bgColor?: BgColorValue;
  defaultContent?: React.ReactNode;
}

export const RightPanelWithAnimation = ({
  show,
  InspectorComponent,
  bgColor,
  defaultContent,
}: RightPanelWithAnimationProps) => {
  const [visible, setVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);

  // Handle show/hide transitions (only relevant when no defaultContent)
  useEffect(() => {
    if (defaultContent !== undefined) {
      return;
    }
    if (show) {
      setShouldRender(true);
      // Slight delay to ensure DOM is updated before animation starts
      setTimeout(() => setVisible(true), 10);
    } else if (!show && shouldRender) {
      setVisible(false);
      // Wait for animation to complete before removing from DOM
      const timer = setTimeout(() => setShouldRender(false), 300); // Match CSS transition duration
      return () => clearTimeout(timer);
    }

    return;
  }, [show, shouldRender, defaultContent]);

  // When defaultContent is provided, the panel is always visible — no mount/unmount animation.
  if (defaultContent !== undefined) {
    return (
      <FormSidePanel
        bgColor={bgColor}
        className={getRightPanelClass()}
        overflowY="auto"
        side="right"
      >
        {InspectorComponent ? (
          <Flex dir="column" p="xl">
            {InspectorComponent}
          </Flex>
        ) : (
          defaultContent
        )}
      </FormSidePanel>
    );
  }

  if (!shouldRender) {
    return null;
  }

  return (
    <FormSidePanel
      bgColor={bgColor}
      className={`${getRightPanelClass()} ${visible ? "" : flowEditorStyles.hidden}`}
      overflowY="auto"
      side="right"
    >
      <Flex dir="column" p="xl">
        {InspectorComponent}
      </Flex>
    </FormSidePanel>
  );
};
